import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../components/auth/AuthRequiredRoute/AuthRequiredRoute";
import ImageInput from "../../components/form/ImageInput/ImageInput";
import Icon from "../../components/ui/Icon/Icon";
import Column from "../../components/ui/Stack/Column";
import HStack from "../../components/ui/Stack/HStack";
import { useLoanInfo } from "../../features/loans/hooks/useLoanInfo";
import { useFileUpload } from "../../hooks/useFileUpload";
import { aunjaiScService } from "../../services/aunjai-sc.service";
import { CreatePayment } from "../../services/modules/loan";
import {
  convertFileInfoToAttachment,
  formatAttachment,
  generateReceiptImageName,
  getLatestInstallment,
  getLoanInstallmentSummary,
} from "../../utils/loan";
import { formatMoneyNumber } from "../../utils/util";
import SuccessSection from "../EditMyLoan/SuccessSection";
import HeaderComponent from "../Header/HeaderComponent";
import HeaderDesktop from "../Header/HeaderDesktop";
import PaymentInfoCard from "./_components/PaymentInfoCard";
import LoadingPage from "../../components/LoadingPage/LoadingPage";
import { useTranslation } from "react-i18next";
import CardPayment from "../../components/payment/CardPayment";

import { useIsDesktop } from "../../hooks/useIsDesktop";
import FooterButton from "../../components/ui/Button/FooterButton";

const PaymentPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("payment");
  const { loanId } = useParams();

  const { userData } = useAuth();

  const { loanInfo, isFetching: isFetchingLoanInfo } = useLoanInfo({
    loanId,
    userData,
  });

  const latestInstallment = getLatestInstallment(loanInfo);

  const { fileInfo, propagateFileChange } = useFileUpload(
    generateReceiptImageName({
      loanId,
      installment: latestInstallment,
    })
  );
  const [isSuccess, setIsSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("transfer");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("status") === "success") {
      setIsSuccess(true);
    }
  }, []);

  const shouldAllowMakingAPayment = loanInfo?.loan_state === "open";

  console.log("loanInfo", loanInfo);

  const loanNumber = loanInfo?.loan_name || "-";

  const dueDate = latestInstallment?.installment_date || "-";

  const { totalInstallments, paidInstallments } =
    getLoanInstallmentSummary(loanInfo);

  const isDesktop = useIsDesktop();

  async function handleUploadReceipt() {
    try {
      if (!fileInfo?.base64) {
        alert(t("alertUpload"));
        return;
      }

      const receiptAttachment = formatAttachment({
        loanId,
        attachment: convertFileInfoToAttachment(fileInfo),
        fileHide: true,
      });

      await aunjaiScService.insertAttachment(receiptAttachment);

      await CreatePayment({
        body: {
          loan_id: Number(loanId),
          installment_id: Number(latestInstallment.installment_name),
        },
      });

      setIsSuccess(true);
    } catch (error) {
      alert(t("fail"));
    }
  }

  if (isFetchingLoanInfo) {
    return <LoadingPage message={t("loading")} />;
  }

  if (!shouldAllowMakingAPayment && loanInfo?.loan_state !== undefined) {
    return <Navigate to={`/my-loans/${loanId}`} />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: isDesktop ? "50%" : "100%",
        background: isDesktop ? "white" : "#FAFAFA",
        margin: isDesktop ? "80px auto 0 auto" : "0",
      }}
    >
      {isDesktop ? (
        <HeaderDesktop title={t("header")} showIcon={true} onBack={true} />
      ) : (
        <HeaderComponent onBack={() => navigate(-1)} title={t("header")} />
      )}

      {isSuccess ? (
        <SuccessSection
          title={t("title")}
          descriptionLine1={t("descriptionLine1")}
          onNext={() => navigate(`/my-loans/${loanId}`)}
          confirmButtonText={t("confirmButtonText")}
          icon={<Icon size={72} name="ico-email" />}
        />
      ) : (
        <>
          <Column gap={12} fullWidth style={{ background: "#fff" }}>
            <HStack
              fullWidth
              style={{ padding: 24 }}
              justify="between"
              align="start"
              gap={12}
            >
              <Column align="start" gap={4} fullWidth>
                <HStack justify="between" fullWidth>
                  <p
                    style={{ fontSize: isDesktop ? 18 : 14, color: "#667085" }}
                  >
                    {t("loanId")}
                  </p>
                  <p
                    style={{ fontSize: isDesktop ? 18 : 14, color: "#101828" }}
                  >
                    {loanNumber}
                  </p>
                </HStack>
                <HStack justify="between" fullWidth>
                  <p
                    style={{ fontSize: isDesktop ? 18 : 14, color: "#667085" }}
                  >
                    {t("period")}
                  </p>
                  <p
                    style={{
                      fontSize: isDesktop ? 18 : 14,
                      color: "#101828",
                      fontWeight: 600,
                    }}
                  >
                    {paidInstallments + 1}/{totalInstallments}
                  </p>
                </HStack>
                <HStack justify="between" fullWidth>
                  <p
                    style={{ fontSize: isDesktop ? 18 : 14, color: "#667085" }}
                  >
                    {t("dueDate")}
                  </p>
                  <p
                    style={{ fontSize: isDesktop ? 18 : 14, color: "#101828" }}
                  >
                    {dueDate}
                  </p>
                </HStack>
                <HStack justify="between" fullWidth>
                  <p
                    style={{ fontSize: isDesktop ? 18 : 14, color: "#667085" }}
                  >
                    {t("installmentAmount")}
                  </p>
                  <p
                    style={{ fontSize: isDesktop ? 18 : 14, color: "#101828" }}
                  >
                    {formatMoneyNumber(latestInstallment?.total_amount || latestInstallment?.principal_remaining + latestInstallment?.interest_remaining || 0)}
                  </p>
                </HStack>
              </Column>
            </HStack>
          </Column>
          {/* Payment Method Tabs */}
          <div
            style={{
              display: "flex",
              gap: 0,
              marginBottom: 0,
              borderBottom: "2px solid var(--color-border, #E6E4E4)",
              background: "#fff",
            }}
          >
            <button
              onClick={() => setPaymentMethod("transfer")}
              style={{
                flex: 1,
                padding: "12px",
                border: "none",
                borderBottom:
                  paymentMethod === "transfer"
                    ? "3px solid var(--color-primary, #FF6500)"
                    : "3px solid transparent",
                background: "none",
                fontSize: 16,
                fontWeight: paymentMethod === "transfer" ? 600 : 400,
                color:
                  paymentMethod === "transfer"
                    ? "var(--color-primary, #FF6500)"
                    : "#667085",
                cursor: "pointer",
              }}
            >
              โอนเงิน
            </button>
            <button
              onClick={() => setPaymentMethod("card")}
              style={{
                flex: 1,
                padding: "12px",
                border: "none",
                borderBottom:
                  paymentMethod === "card"
                    ? "3px solid var(--color-primary, #FF6500)"
                    : "3px solid transparent",
                background: "none",
                fontSize: 16,
                fontWeight: paymentMethod === "card" ? 600 : 400,
                color:
                  paymentMethod === "card"
                    ? "var(--color-primary, #FF6500)"
                    : "#667085",
                cursor: "pointer",
              }}
            >
              บัตรเครดิต
            </button>
          </div>

          {paymentMethod === "transfer" && (
            <>
              {loanInfo && <PaymentInfoCard loanInfo={loanInfo} />}
              {/* Upload Section */}
              <Column
                gap={8}
                style={{ padding: 24, background: "#fff", marginTop: 16 }}
              >
                <ImageInput
                  label={<span>{t("label")}</span>}
                  description={t("description")}
                  remark={t("remark")}
                  {...propagateFileChange()}
                  placeholder={t("placeholder")}
                />
              </Column>
              <FooterButton
                onSubmit={handleUploadReceipt}
                submitButtonTitle={t("confirm")}
              />
            </>
          )}

          {paymentMethod === "card" && (
            <div style={{ padding: 24, background: "#fff" }}>
              <CardPayment
                loanId={loanId}
                installment={latestInstallment}
                customerEmail={userData?.email}
              />
            </div>
          )}
        </>
      )}
      {/* Loan Info */}
    </div>
  );
};

export default PaymentPage;
